<template>
    <BCard no-body v-show="enrollments">
        <BCardBody class="d-flex justify-content-between">
            <div>
                <BCardTitle>Alumnos</BCardTitle>
                <BCardSubTitle>Listado de alumnos matrículados</BCardSubTitle>
            </div>
            <div>
                <BFormInput v-model="searchQuery" class="d-inline-block mr-1" :placeholder="$t('Search') + '...'" />
            </div>
            <div>
                <BDropdown
                id="dropdown-3"
                text="Exportar"
                variant="success"
                class="mr-1"
                >
                    <BDropdownItem @click="exportData('/courses/export', {id: course.id, relation: 'enrollments', type: 'PDF' }, `Alumnos ${course.name}.pdf`, 'PDF')">PDF</BDropdownItem>
                    <BDropdownItem @click="exportData('/courses/export', {id: course.id, relation: 'enrollments', type: 'CSV' }, `Alumnos ${course.name}.csv`, 'CSV')">CSV</BDropdownItem>
                    <BDropdownItem @click="exportData('/courses/export', {id: course.id, relation: 'enrollments', type: 'XLS' }, `Alumnos ${course.name}.xls`, 'XLS')">XLS</BDropdownItem>
                </BDropdown>
            </div>
        </BCardBody>

        <BTable v-if="enrollments" ref="enrollmentsTable" striped id="coursesTable" variant="secondary" :items="fetchCourseEnrollments" :fields="fields" show-empty empty-text="Ningún alumno matriculado"> 
            <template #cell(student)="data">
                <BLink :to="{ name: 'enrollments-view', params: { id: data.item.id } }" class="font-weight-bold d-block text-nowrap" > <b>{{ data.item.student.full_name }}</b> </BLink>
                <small> {{formatDate(data.item.start_date)}} </small> <br>
                <small> {{ data.item.end_date ? formatDate(data.item.end_date): '' }}  </small>
            </template>

            <template v-for="(ev) in evaluations"   v-slot:[`cell(${ev.id})`]="data">
                <div>
                    <BButton v-if="(getStudentGrade(ev, data.item.id)).exists" variant="outlined"  @click.prevent="showData(ev, data.item.id)"  @click="data.toggleDetails()">
                        <span>{{(getStudentGrade(ev, data.item.id)).grade.grade ? (getStudentGrade(ev, data.item.id)).grade.grade : 'Exento'}}</span>
                    </BButton>
                    <BButton v-else variant="flat-success" class="btn-icon" @click.prevent="showAdd(ev.id, data.item.id)" @click="data.toggleDetails()">
                        <FeatherIcon size="18" icon="PlusIcon" />
                    </BButton>  
                </div>
            </template>

            <template #row-details="data">
                <BRow>
                    <BCol cols="6" v-if="showDetails">
                        <BCard class="border rounded ">
                            <BCol cols="12" v-if="grade.grade"><strong>Nota:</strong> {{ grade.grade }} </BCol>
                            <BCol cols="12"><strong>Observaciones:</strong> {{ grade.observations }} </BCol>
                            <BCol class="mt-2">
                                <BButton @click="showEditForm = true"> Modificar </BButton>
                                <BButton @click="deletePopupAxios(grade.id, `grades/${grade.id}`)" class="ml-2" variant="outline-danger"> Eliminar </BButton>
                            </BCol>
                        </BCard>
                    </BCol>

                    <BCol cols="6" v-if="showAddForm || showEditForm">
                        <BCard class="border rounded">
                            <ValidationObserver ref="gradeForm">
                                <BForm> 
                                    <BRow>
                                        <CheckboxValidated vsMd="4"  vsName="Es evaluable" :vsModel.sync="grade.is_assessable"  vsLabel="¿Es evaluable?" />
                                    </BRow>
                                    <BRow>
                                        <NumberInputValidated v-if="grade.is_assessable" vsName="Nota" :vsModel.sync="grade.grade" vsLabel="Peso" vsMd="4" :vsRequired="grade.is_assessable ? false : true" :vsRules="grade.is_assessable ? '' : 'required'" />
                                        <TextareaValidated  vsName="Notes" :vsModel.sync="grade.observations" vsMd="12" />
                                    </BRow>
                                    <BButton v-if="showAddForm" @click="onSubmit(data, 'add', data.item.id)">Guardar</BButton>
                                    <BButton v-if="showEditForm" @click="onSubmit(data, 'edit')">Modificar</BButton>

                                </BForm>
                            </ValidationObserver>
                        </BCard>
                    </BCol>
                </BRow>
            </template>

            <template #cell(average)="data">

                <div class="text-center">
                    <span> <b>{{getAverage(data.item.id).ten}}</b></span> 
                    <br>
                    <span> {{ getAverage(data.item.id).part}}/{{ getAverage(data.item.id).total}}</span>

                </div>
            </template>
        </BTable>
        <BPagination class="m-2" v-model="currentPage" :total-rows="totalRows" :per-page="perPage" aria-controls="coursesTable">                
            <template #prev-text> <feather-icon icon="ChevronLeftIcon" size="18" /> </template>
            <template #next-text> <feather-icon icon="ChevronRightIcon" size="18"/> </template>
        </BPagination>
    </BCard>
</template>

<script>
import { inject, ref, watch, onMounted } from '@vue/composition-api';
import axios from '@axios';
import { BCard, BCardBody, BCardSubTitle, BCardTitle, BDropdown, BDropdownItem, BDropdownItemButton, BFormInput, BPagination, BTable, BButton, BForm, BRow, BCol, BLink} from 'bootstrap-vue';
import exportUtils from '@/hooks/exportUtils';
import dateUtils from '@/hooks/dateUtils'
import { useCallback } from 'preact/hooks';
import { getEvaluations } from '@/composables/evaluationService';
import NumberInputValidated from '@/components/forms/NumberInputValidated.vue';
import CheckboxValidated from '@/components/forms/CheckboxValidated.vue';
import TextareaValidated from '@/components/forms/TextareaValidated.vue';
import { addGrade, updateGrade } from '@/composables/gradeService';
import popups from '@/hooks/popups';
import { ValidationObserver } from 'vee-validate'

export default {
    components: { BTable, BPagination, BCard, BCardBody, BCardTitle, BCardSubTitle, BFormInput, BDropdown, BDropdownItem, BDropdownItemButton, BButton, BForm, NumberInputValidated, CheckboxValidated, TextareaValidated, BRow, BCol, ValidationObserver, BLink },
    setup() {

        const { successToast, errorToast, deletePopupAxios } = popups()
        const { exportData } = exportUtils()
        const gradeForm = ref(null)
        const showAddForm = ref(null)
        const showEditForm = ref(null)
        const showDetails = ref(null)
        const enrollmentsTable = ref(null)
        const course = inject('course')
        const evaluations = ref(null)
        const totalRows = ref(null)
        const enrollments = ref([])
        const currentPage = ref(1)
        const searchQuery = ref("")
        const perPage = ref(10)
        const { formatDate } = dateUtils()
        const fields = ref([
            { key: 'student', label: 'Alumno' }
        ])



        const grade = ref({
            evaluation_id: null,
            enrollment_id: null,
            grade: null,
            is_assessable: true,
            observations: '',
            enrollment_id: null
        })

        const showData = (ev, enrol_id) => {
            showDetails.value=true
            showAddForm.value=false
            showEditForm.value=false
            grade.value = ev.grades[index]
            let index = ev.grades.findIndex(grade => grade.enrollment_id == enrol_id)
            if (index > -1) {
                grade.value = ev.grades[index] 
            } 
        }

        const getAverage = (enrollment_id) => {
            let filteredGrades = []
            if (evaluations.value.length==0) {
                return {ten: 0, part: 0, total: 0}
            } else {
                evaluations.value.forEach(ev => {
                    let gr = ev.grades.find(gr => gr.enrollment_id==enrollment_id ?? null)
                    filteredGrades.push({grade: gr, weight: ev.weight})
                })
    
                let totalWeight = 0
                let sum = 0
                filteredGrades.forEach(mark => {
                    totalWeight+=parseFloat(mark.weight)
    
                    if (mark.grade) {
                        sum += (mark.grade.grade * (mark.weight/100))*10
                    }
                })
    
                let tenAverage= sum*10/totalWeight
    
                return {ten: tenAverage.toFixed(2), part: parseInt(sum), total: parseInt(totalWeight)}
            }

        }

        const onSubmit = (data, action, enrollment_id) => {
            gradeForm.value.validate().then((success) => {
                if (success) {
                    if (action=="add") {
                        grade.value.enrollment_id = enrollment_id
                        createGrade(grade.value, data)
                    } else {
                        editGrade(grade.value, data)
                    }

                }
            })
        }

        const getStudentGrade = (ev, enrol_id) => {

            let index = ev.grades.findIndex(grade => grade.enrollment_id == enrol_id)
            
            if (index > -1) {
                return { exists: true,  grade: ev.grades[index] }
            } else {
              return { exists: false, grade: null}
            }
        }

        const createGrade = async function (newGrade, data) {
            let response = await addGrade(newGrade)
            if (response.data.success) {
                successToast("Nota creada correctamente")
                refetchData()
                data.toggleDetails()
            } else {

                if (response.data.code && response.data.code=="NOT_ACTIVE_ENROLLMENT" ) {
                    errorToast("La matrícula de este alumno/a no estará activa en esta evaluación. Cambia las fechas.")
                } else {
                    errorToast("Error creando nota")
                }
            }
        }

        const editGrade = async function (editGrade, data) {
            let response = await updateGrade(editGrade)
            if (response.data.success) {
                successToast("Nota actualizada correctamente")
                refetchData()
                data.toggleDetails()
            } else {
                errorToast("Error actualizando nota")
            }
        }
        
        const refetchData = function() {
            enrollmentsTable.value.refresh()
        }


        const query = ref({
            q: searchQuery.value,
            course: course.value.id,
            perPage: 10,
            page: currentPage.value
        })



        const showAdd = (ev_id, enrollment_id) => {

            grade.value = {
                evaluation_id : ev_id,
                enrollment_id: enrollment_id,
                grade: null,
                is_assessable: true,
                observations: '',
            }
            
            showDetails.value = false
            showEditForm.value = false
            showAddForm.value = true
        }

        const fetchCourseEnrollments = function(ctx, callback) {
            axios.get('/enrollments', { params: query.value }).then(response => {
                enrollments.value = response.data.enrollments
                totalRows.value = response.data.total
                callback(enrollments.value)
            })

            fetchEvaluations()
        }

        const fetchEvaluations = async function () {
            let response = await getEvaluations({
                // q: searchQuery.value,
                // perPage: perPage.value,
                // page: currentPage.value,
                sortBy: 'start_date',
                sortDesc: false,
                course: course.value.id
            })
            // totalData.value = response.data.total
            evaluations.value = response.data.evaluations
            evaluations.value.forEach(ev => {
                fields.value.push({key: ev.id.toString(), label: ev.name})
            })

            fields.value.push({key: 'average', label: "Media", class:'text-center'})

            return evaluations.value
        }

        fetchEvaluations()
              

        watch([searchQuery, currentPage], val => {
            query.value.q = searchQuery.value
            query.value.page = currentPage.value
            refetchData()
        })


        return {
            gradeForm,
            searchQuery,
            enrollments,
            currentPage,
            exportData,
            totalRows,
            perPage,
            fields,
            course,
            formatDate,
            enrollmentsTable,
            refetchData,
            fetchCourseEnrollments,
            evaluations,
            showAdd,
            grade,
            onSubmit, 
            getStudentGrade, 
            showAddForm, 
            showDetails,
            showData,
            showEditForm,
            deletePopupAxios,
            getAverage
        };
    },
}
</script>
